import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "@geoman-io/leaflet-geoman-free";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";
import {
  BoxSelect,
  CircleDot,
  Copy,
  Home,
  Maximize2,
  MousePointer2,
  Pencil,
  PersonStanding,
  SlidersHorizontal,
  Spline,
  Trash2,
} from "lucide-react";
import {
  ClosedAreasLayer,
  createGugikLayers,
  GddkiaDataLayer,
  GugikLayerPanel,
  useGugikLoading,
  useGugikState,
  useGugikToggle,
} from "@/components/map/gugik";
import { MapStatusBar } from "@/components/map/MapStatusBar";
import { detachRemovedLayer, hasVertices } from "@/components/map/geometry";
import { useStreetViewTool } from "@/components/map/streetView";
import type {
  NodeKind,
  PoleParams,
  PoleResult,
  TechCalcDictionaries,
  TechCalcDrawing,
  TechCalcFeature,
  TechCalcLineProperties,
  TechCalcPointProperties,
} from "../types";
import { fmtBundle, fmtMeters, nextRef, poleDisplayLabel } from "../utils";
import { BulkParamsPanel } from "./BulkParamsPanel";
import { PoleParamsPanel } from "./PoleParamsPanel";
import { SpanParamsPanel } from "./SpanParamsPanel";

export type EditorMode =
  | "pole"
  | "access"
  | "span"
  | "copy"
  | "select"
  | "edit"
  | "remove"
  | "params"
  | "streetview"
  | "off";

/** Klik-cel trybów Usuń/Edytuj — słupy i przęsła dzielą wierzchołki, więc
 *  użytkownik musi móc powiedzieć, W CO celuje. */
export type EditTarget = "all" | "nodes" | "spans";

const POLE_COLOR = "#2563eb";
const ACCESS_COLOR = "#ea580c";
const OK_COLOR = "#16a34a";
const FAIL_COLOR = "#dc2626";
const NA_COLOR = "#9ca3af";
const SPAN_STYLE: L.PathOptions = { color: "#475569", weight: 3, opacity: 0.9 };
/** Access-line span (słup → punkt odbioru): thinner + dashed to tell it apart. */
const ACCESS_SPAN_STYLE: L.PathOptions = {
  color: "#475569",
  weight: 2,
  dashArray: "6 4",
  opacity: 0.9,
};
/** Pixel radius within which a drawn span end "hits" a node. */
const SNAP_PX = 25;

type FeatureLayer = L.Layer & {
  feature?: { type: "Feature"; properties: TechCalcPointProperties | TechCalcLineProperties };
};

const pointProps = (l: L.Layer): TechCalcPointProperties =>
  (l as FeatureLayer).feature!.properties as TechCalcPointProperties;
const lineProps = (l: L.Layer): TechCalcLineProperties =>
  (l as FeatureLayer).feature!.properties as TechCalcLineProperties;

/** Attach contract properties to a layer. Goes through FeatureLayer (not the
 *  concrete L.Marker/L.Polyline type) so we don't have to fake a geometry —
 *  serialization always reads geometry from the live layer, never from here. */
const setFeature = (
  l: L.Layer,
  properties: TechCalcPointProperties | TechCalcLineProperties,
) => {
  (l as FeatureLayer).feature = { type: "Feature", properties };
};

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

/** Poles are circles, access lines are diamonds — distinguishable even color-blind.
 *  `hollow` renders an outline-only shape (słup spoza opracowania). */
function nodeSvg(kind: NodeKind, color: string, hollow = false, size = 18): string {
  const c = size / 2;
  const fill = hollow ? "#ffffff" : color;
  const stroke = hollow ? color : "#fff";
  const shape =
    kind === "pole"
      ? `<circle cx="${c}" cy="${c}" r="${c - 2.5}" fill="${fill}" stroke="${stroke}" stroke-width="2"/>`
      : `<rect x="${c - 5}" y="${c - 5}" width="10" height="10" transform="rotate(45 ${c} ${c})" fill="${fill}" stroke="${stroke}" stroke-width="2"/>`;
  return `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">${shape}</svg>`;
}

function nodeIcon(kind: NodeKind, color: string, hollow = false): L.DivIcon {
  return L.divIcon({
    html: nodeSvg(kind, color, hollow),
    className: "tc-node-icon",
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });
}

/** Refs are strictly auto-assigned S#/A#, so the prefix identifies an access span. */
const isAccessSpan = (p: TechCalcLineProperties): boolean =>
  p.from_ref.startsWith("A") || p.to_ref.startsWith("A");

const spanStyle = (p: TechCalcLineProperties): L.PathOptions =>
  isAccessSpan(p) ? ACCESS_SPAN_STYLE : SPAN_STYLE;

function flatLatLngs(layer: L.Polyline): L.LatLng[] {
  const raw = layer.getLatLngs();
  return Array.isArray(raw[0]) ? (raw as L.LatLng[][]).flat() : (raw as L.LatLng[]);
}

function polylineLengthM(lls: L.LatLng[]): number {
  let total = 0;
  for (let i = 1; i < lls.length; i++) total += lls[i - 1].distanceTo(lls[i]);
  return total;
}

/** Ray-casting point-in-polygon on plain lat/lng (selection polygons are small). */
function pointInRing(pt: L.LatLng, ring: L.LatLng[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i].lng;
    const yi = ring[i].lat;
    const xj = ring[j].lng;
    const yj = ring[j].lat;
    const intersects =
      yi > pt.lat !== yj > pt.lat &&
      pt.lng < ((xj - xi) * (pt.lat - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

export interface TechCalcMapHandle {
  /** Merge param patches into poles by ref (used by "Zasugeruj funkcje"). */
  applyPoleParams: (updates: Record<string, Partial<PoleParams>>) => void;
}

interface Props {
  initialDrawing: TechCalcDrawing | null | undefined;
  dictionaries: TechCalcDictionaries | undefined;
  /** Full drawing lifted up on every change; dirty=false only for the initial load. */
  onChange: (fc: TechCalcDrawing, dirty: boolean) => void;
  /** Per-pole results (results tab): green passed / red failed / gray error. */
  results?: Record<string, PoleResult> | null;
  /** True on the results tab: markers get status colors and editing is locked. */
  showResults?: boolean;
  className?: string;
}

/**
 * Leaflet + Leaflet-Geoman editor for one tech-calc variant: poles (S1, S2…) and
 * abonent access lines (A1, A2…) as markers, spans (przęsła) as lines whose ends
 * snap to nodes and carry from_ref/to_ref. The whole drawing serializes to the
 * contract GeoJSON FeatureCollection (WGS84) and is lifted up via onChange.
 * Parameter editing docks in a sidebar to the RIGHT of the map (the map narrows) —
 * overlays covering the drawing were explicitly rejected by the owner.
 */
export const TechCalcMap = forwardRef<TechCalcMapHandle, Props>(function TechCalcMap(
  {
    initialDrawing,
    dictionaries,
    onChange,
    results,
    showResults = false,
    className,
  }: Props,
  handleRef,
) {
  const mapRef = useRef<L.Map | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const groupRef = useRef<L.FeatureGroup | null>(null);
  const gugikParcelsRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikInfraRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikOrthoRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikMpzpRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikGdosRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikKonserwatorRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikKonserwatorEwidencjaRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikGddkiaRef = useRef<GddkiaDataLayer | null>(null);
  const gugikClosedAreasRef = useRef<ClosedAreasLayer | null>(null);
  // StrictMode-safe "loaded initialDrawing" guard — compare the group INSTANCE (see
  // ZEDrawingMap for the full story: a boolean survives the setup/cleanup/setup cycle
  // and would leave the rebuilt map empty).
  const loadedIntoRef = useRef<L.FeatureGroup | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const dictionariesRef = useRef(dictionaries);
  dictionariesRef.current = dictionaries;
  const resultsRef = useRef(results);
  resultsRef.current = results;
  const showResultsRef = useRef(showResults);
  showResultsRef.current = showResults;

  const [mode, setMode] = useState<EditorMode>("pole");
  const modeRef = useRef<EditorMode>("pole");
  // Live map instance for React children (the status bar) — set after L.map().
  const [mapInstance, setMapInstance] = useState<L.Map | null>(null);
  // Klik-cel trybów Usuń/Edytuj (słupy vs przęsła vs wszystko).
  const [editTarget, setEditTarget] = useState<EditTarget>("all");
  const [zoom, setZoom] = useState(6);
  const [gugik, setGugik] = useGugikState("techCalc.drawing", {
    parcels: true,
    infra: false,
    ortho: false,
    mpzp: false,
    gdos: false,
    konserwator: false,
    konserwatorEwidencja: false,
    gddkia: false,
    closedAreas: false,
  });
  const [drawError, setDrawError] = useState<string | null>(null);
  const [paramsEditor, setParamsEditor] = useState<{ layer: FeatureLayer } | null>(null);
  // Poligonowe zaznaczenie słupów (tryb "select") — refs + amber halos on the map.
  const [selection, setSelection] = useState<string[]>([]);
  const selectionHaloRef = useRef<L.LayerGroup | null>(null);
  // Tryb "Kopiuj słup": ref słupa źródłowego (stemplowanie kopii do zmiany trybu).
  const [copySource, setCopySource] = useState<string | null>(null);
  const copySourceRef = useRef<string | null>(null);
  copySourceRef.current = copySource;
  const copyHaloRef = useRef<L.LayerGroup | null>(null);
  // Bump to re-render React bits that mirror layer state (e.g. panel after restyle).
  const [, setLayerEpoch] = useState(0);

  // ── icons + tooltips ───────────────────────────────────────────────────────
  /** Marker icon honoring out_of_scope (hollow gray) and the results view. */
  const iconForNode = useCallback(
    (p: TechCalcPointProperties): L.DivIcon => {
      const outOfScope = p.kind === "pole" && !!p.params.out_of_scope;
      let color = p.kind === "pole" ? POLE_COLOR : ACCESS_COLOR;
      if (outOfScope) {
        color = NA_COLOR;
      } else if (showResultsRef.current && p.kind === "pole") {
        const r = resultsRef.current?.[p.ref];
        color = !r || !r.ok ? NA_COLOR : r.passed ? OK_COLOR : FAIL_COLOR;
      }
      return nodeIcon(p.kind, color, outOfScope);
    },
    [],
  );

  const restyleNodes = useCallback(() => {
    groupRef.current?.getLayers().forEach((layer) => {
      if (layer instanceof L.Marker) layer.setIcon(iconForNode(pointProps(layer)));
    });
  }, [iconForNode]);

  const refreshTooltips = useCallback(() => {
    groupRef.current?.getLayers().forEach((layer) => {
      layer.unbindTooltip();
      if (layer instanceof L.Marker) {
        const p = pointProps(layer);
        // Etykieta wg priorytetu: nr historyczny → nr pełny → ref.
        const label = poleDisplayLabel(p);
        const subParts: string[] = [];
        if (label !== p.ref) subParts.push(p.ref);
        if (p.params.pole_type) subParts.push(p.params.pole_type);
        if (p.kind === "pole" && p.params.is_lighting) subParts.push("oświetleniowy");
        if (p.kind === "pole" && p.params.out_of_scope) subParts.push("poza opracowaniem");
        const sub = subParts.length
          ? `<span class="tc-label-sub">${escapeHtml(subParts.join(" · "))}</span>`
          : "";
        layer.bindTooltip(`<b>${escapeHtml(label)}</b>${sub}`, {
          permanent: true,
          direction: "top",
          offset: L.point(0, -10),
          className: "tc-node-label",
          interactive: false,
        });
      } else if (layer instanceof L.Polyline) {
        // Przęsło bez wierzchołków (Geoman zostawia takie po skasowaniu wszystkich punktów)
        // nie ma środka — bindTooltip z permanent:true woła getCenter(), które rzuca
        // "latlngs not passed" i wywala całą stronę. Zob. components/map/geometry.ts.
        if (!hasVertices(layer)) return;
        const p = lineProps(layer);
        const len = p.length_override ?? p.length_m;
        const bundlesHtml = p.bundles.length
          ? `<span class="tc-label-sub">${escapeHtml(p.bundles.map(fmtBundle).join(", "))}</span>`
          : "";
        layer.bindTooltip(
          `${fmtMeters(len)}${p.length_override != null ? " ✎" : ""}${bundlesHtml}`,
          { permanent: true, direction: "center", className: "tc-line-label", interactive: false },
        );
      }
    });
  }, []);

  // ── serialization ──────────────────────────────────────────────────────────
  const emit = useCallback(
    (dirty: boolean) => {
      const group = groupRef.current;
      if (!group) return;
      const features: TechCalcFeature[] = [];
      group.getLayers().forEach((layer) => {
        if (layer instanceof L.Marker) {
          const ll = layer.getLatLng();
          features.push({
            type: "Feature",
            geometry: { type: "Point", coordinates: [ll.lng, ll.lat] },
            properties: pointProps(layer),
          });
        } else if (layer instanceof L.Polyline) {
          // Przęsło bez wierzchołków to pozostałość po usuwaniu punktów w Geomanie, nie
          // rysunek użytkownika — nie zapisujemy go, bo wraca jako coordinates: [] i psuje
          // edytor przy następnym otwarciu.
          if (!hasVertices(layer)) return;
          const lls = flatLatLngs(layer);
          const p = lineProps(layer);
          p.length_m = Math.round(polylineLengthM(lls) * 10) / 10;
          features.push({
            type: "Feature",
            geometry: { type: "LineString", coordinates: lls.map((l) => [l.lng, l.lat]) },
            properties: p,
          });
        }
      });
      onChangeRef.current({ type: "FeatureCollection", features }, dirty);
      refreshTooltips();
      setLayerEpoch((n) => n + 1);
    },
    [refreshTooltips],
  );
  const emitRef = useRef(emit);
  emitRef.current = emit;

  // ── node lookups ───────────────────────────────────────────────────────────
  const nodeLayers = (): L.Marker[] =>
    (groupRef.current?.getLayers() ?? []).filter((l): l is L.Marker => l instanceof L.Marker);

  const findNode = (ref: string): L.Marker | undefined =>
    nodeLayers().find((m) => pointProps(m).ref === ref);

  const nearestNode = (latlng: L.LatLng, maxPx: number): L.Marker | null => {
    const map = mapRef.current;
    if (!map) return null;
    const pt = map.latLngToContainerPoint(latlng);
    let best: L.Marker | null = null;
    let bestDist = maxPx;
    for (const m of nodeLayers()) {
      const d = pt.distanceTo(map.latLngToContainerPoint(m.getLatLng()));
      if (d <= bestDist) {
        best = m;
        bestDist = d;
      }
    }
    return best;
  };

  /** Move every span end that references `ref` onto the node's current position. */
  const syncSpansToNode = (ref: string, latlng: L.LatLng) => {
    groupRef.current?.getLayers().forEach((layer) => {
      if (!(layer instanceof L.Polyline)) return;
      const p = lineProps(layer);
      if (p.from_ref !== ref && p.to_ref !== ref) return;
      const lls = flatLatLngs(layer);
      if (p.from_ref === ref) lls[0] = latlng;
      if (p.to_ref === ref) lls[lls.length - 1] = latlng;
      layer.setLatLngs(lls);
    });
  };

  /**
   * After a span's vertices were edited: re-derive from/to by the nearest node under
   * each end (allows re-attaching to another pole); an end dropped in the void, or an
   * invalid pair (same node / access–access), snaps back to the previous nodes.
   */
  const resolveSpanEndpoints = (layer: L.Polyline) => {
    const p = lineProps(layer);
    const lls = flatLatLngs(layer);
    const candA = nearestNode(lls[0], SNAP_PX) ?? findNode(p.from_ref);
    const candB = nearestNode(lls[lls.length - 1], SNAP_PX) ?? findNode(p.to_ref);
    let a = candA;
    let b = candB;
    const invalid =
      !a || !b || a === b || (pointProps(a).kind === "access" && pointProps(b).kind === "access");
    if (invalid) {
      a = findNode(p.from_ref);
      b = findNode(p.to_ref);
      setDrawError("Koniec przęsła musi trafiać w słup — przywrócono poprzednie zaczepienie.");
    }
    if (!a || !b) return;
    p.from_ref = pointProps(a).ref;
    p.to_ref = pointProps(b).ref;
    lls[0] = a.getLatLng();
    lls[lls.length - 1] = b.getLatLng();
    layer.setLatLngs(lls);
    // Re-attaching can flip a span between pole–pole and pole–access.
    layer.setStyle(spanStyle(p));
  };

  const onLayerEdited = (e: L.LeafletEvent) => {
    const layer = e.target as L.Layer;
    if (layer instanceof L.Marker) {
      syncSpansToNode(pointProps(layer).ref, layer.getLatLng());
    } else if (layer instanceof L.Polyline) {
      resolveSpanEndpoints(layer);
    }
    emitRef.current(true);
  };
  const onLayerEditedRef = useRef(onLayerEdited);
  onLayerEditedRef.current = onLayerEdited;

  const attachLayer = (layer: L.Layer) => {
    const handler = (e: L.LeafletEvent) => onLayerEditedRef.current(e);
    layer.on("pm:edit", handler);
    layer.on("pm:dragend", handler);
    layer.on("pm:markerdragend", handler);
  };

  /**
   * Default conductor bundle for a NEW span. Legacy-engine reality: the norm
   * catalog knows typical multi-core sets (4×AL, 4×AsXSn…) — a single-core
   * bundle has no match and the computation errors out. Hence trunk spans seed
   * AsXSn 35 ×4 and access lines AsXSn 25 ×2 (both tunable in "Parametry").
   */
  const defaultBundleFor = (access: boolean) => {
    const d = dictionariesRef.current;
    const family = d?.families.includes("AsXSn") ? "AsXSn" : (d?.families[0] ?? "AsXSn");
    const wanted = access ? 25 : 35;
    const crossSection = d?.cross_sections.includes(wanted)
      ? wanted
      : (d?.cross_sections[0] ?? wanted);
    return [{ family, cross_section: crossSection, count: access ? 2 : 4 }];
  };

  const fitToDrawing = () => {
    const map = mapRef.current;
    const group = groupRef.current;
    if (!map || !group) return;
    const b = group.getBounds();
    if (b.isValid()) map.fitBounds(b.pad(0.25), { maxZoom: 18 });
  };

  // ── map init (once) ────────────────────────────────────────────────────────
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, {
      center: [52.0, 19.0],
      zoom: 6,
      attributionControl: false,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 21 }).addTo(map);
    groupRef.current = L.featureGroup().addTo(map);
    // Amber halos of the polygon selection — purely visual, never serialized.
    selectionHaloRef.current = L.layerGroup().addTo(map);
    // Blue halo of the copy-source pole (tryb "Kopiuj słup").
    copyHaloRef.current = L.layerGroup().addTo(map);
    const g = createGugikLayers(map);
    gugikParcelsRef.current = g.parcels;
    gugikInfraRef.current = g.infra;
    gugikOrthoRef.current = g.ortho;
    gugikMpzpRef.current = g.mpzp;
    gugikGdosRef.current = g.gdos;
    gugikKonserwatorRef.current = g.konserwator;
    gugikKonserwatorEwidencjaRef.current = g.konserwatorEwidencja;
    gugikGddkiaRef.current = g.gddkia;
    gugikClosedAreasRef.current = g.closedAreas;
    map.on("zoomend", () => setZoom(map.getZoom()));
    mapRef.current = map;
    setMapInstance(map);

    map.pm.setLang("pl");
    map.pm.setGlobalOptions({ snappable: true, snapDistance: 20, continueDrawing: true });

    // "Przyłącze" always has exactly 2 vertices / 1 edge: finish the line the
    // moment the SECOND vertex lands (no double-click). Geoman 2.19 has no public
    // maxVertices option, so we finish via the draw instance on pm:vertexadded —
    // wrapped defensively, drawing continues stamp-style (global continueDrawing).
    map.on("pm:drawstart", (e: { workingLayer: L.Layer; shape: string }) => {
      if (modeRef.current !== "access") return;
      const wl = e.workingLayer as L.Polyline;
      wl.on("pm:vertexadded", () => {
        const count = (wl.getLatLngs() as L.LatLng[]).length;
        if (count < 2) return;
        setTimeout(() => {
          try {
            (
              map.pm as unknown as {
                Draw?: { Line?: { _finishShape?: () => void } };
              }
            ).Draw?.Line?._finishShape?.();
          } catch {
            /* draw already finished/cancelled */
          }
        }, 0);
      });
    });

    // Tryb "Kopiuj słup": klik w mapę stawia kopię źródła (markery nie bąbelkują
    // do mapy, więc klik w słup trafia w handler wyboru źródła, nie tutaj).
    map.on("click", (e: L.LeafletMouseEvent) => {
      if (modeRef.current !== "copy") return;
      const group = groupRef.current;
      if (!group) return;
      const srcRef = copySourceRef.current;
      const src = srcRef ? findNode(srcRef) : undefined;
      if (!src) {
        setDrawError("Najpierw kliknij słup źródłowy, potem klikaj lokalizacje kopii.");
        return;
      }
      const srcProps = pointProps(src);
      const usedRefs = nodeLayers().map((n) => pointProps(n).ref);
      const ref = nextRef(usedRefs, "pole");
      const props: TechCalcPointProperties = {
        ref,
        kind: "pole",
        label: ref,
        // Kopiują się parametry obliczeniowe + flaga out_of_scope; numery są
        // indywidualne (puste), adres celowo pominięty (do pobrania OSM-em).
        params: { ...srcProps.params, full_number: "", historical_number: "" },
      };
      const marker = L.marker(e.latlng, { icon: nodeIcon("pole", POLE_COLOR) });
      setFeature(marker, props);
      marker.setIcon(iconForNode(props));
      group.addLayer(marker);
      attachLayer(marker);
      emitRef.current(true);
    });

    // Geoman odpina usuwaną warstwę TYLKO od mapy — nasza FeatureGroup trzyma ją dalej,
    // więc bez tego "usunięty" element zostaje w rysunku: przęsło z zerem wierzchołków po
    // skasowaniu punktów, albo cały słup, który po zapisie wraca jak gdyby nigdy nic.
    map.on("pm:remove", (e: { layer?: L.Layer }) => {
      detachRemovedLayer(groupRef.current, e?.layer);
      emitRef.current(true);
    });

    map.on("pm:create", (e: { layer: L.Layer }) => {
      const m = modeRef.current;
      const group = groupRef.current;
      if (!group) return;
      const layer = e.layer as FeatureLayer;

      // "Zaznacz": the polygon is a SELECTION shape, not part of the drawing.
      // Must come first — L.Polygon extends L.Polyline, so the span/access
      // branches would otherwise swallow it.
      if (m === "select" && layer instanceof L.Polygon) {
        const rings = layer.getLatLngs() as L.LatLng[] | L.LatLng[][];
        const ring = (Array.isArray(rings[0]) ? (rings as L.LatLng[][])[0] : (rings as L.LatLng[])) ?? [];
        if (map.hasLayer(layer)) map.removeLayer(layer);
        const halo = selectionHaloRef.current;
        halo?.clearLayers();
        const refs: string[] = [];
        for (const node of nodeLayers()) {
          const p = pointProps(node);
          if (p.kind !== "pole") continue; // przyłącza (A#) poza zaznaczeniem
          if (!pointInRing(node.getLatLng(), ring)) continue;
          refs.push(p.ref);
          halo?.addLayer(
            L.circleMarker(node.getLatLng(), {
              radius: 13,
              color: "#f59e0b",
              weight: 2.5,
              fill: false,
              pmIgnore: true,
            } as L.CircleMarkerOptions),
          );
        }
        setSelection(refs);
        if (!refs.length) setDrawError("W narysowanym obszarze nie ma żadnego słupa.");
        return;
      }

      if (m === "pole" && layer instanceof L.Marker) {
        const usedRefs = nodeLayers().map((n) => pointProps(n).ref);
        const ref = nextRef(usedRefs, "pole");
        setFeature(layer, {
          ref,
          kind: "pole",
          label: ref,
          params: { pole_type: "", construction: "", material: "", height: "", function: "" },
        });
        layer.setIcon(nodeIcon("pole", POLE_COLOR));
        group.addLayer(layer);
        attachLayer(layer);
        emitRef.current(true);
        return;
      }

      // "Przyłącze (linia od słupa)": ONE gesture creates the access node at the
      // line's end AND the pole→A# span — same data structure as before, only the
      // gesture changed (owner feedback: an access line is a line, not a point).
      if (m === "access" && layer instanceof L.Polyline) {
        const lls = flatLatLngs(layer);
        const start = lls.length >= 2 ? nearestNode(lls[0], SNAP_PX) : null;
        if (!start || pointProps(start).kind !== "pole") {
          if (map.hasLayer(layer)) map.removeLayer(layer);
          setDrawError("Przyłącze zaczyna się na słupie — pierwszy punkt musi trafić w słup.");
          return;
        }
        // Only the LAST vertex counts as the delivery point; spans are straight.
        const end = lls[lls.length - 1];
        const usedRefs = nodeLayers().map((n) => pointProps(n).ref);
        const ref = nextRef(usedRefs, "access");
        const node = L.marker(end, { icon: nodeIcon("access", ACCESS_COLOR) });
        setFeature(node, {
          ref,
          kind: "access",
          label: ref,
          params: { pole_type: "", construction: "", material: "", height: "", function: "" },
        });
        group.addLayer(node);
        attachLayer(node);

        layer.setLatLngs([start.getLatLng(), end]);
        layer.setStyle(ACCESS_SPAN_STYLE);
        // Typical abonent access line: AsXSn 25 ×2 żyły — tuned in "Parametry".
        setFeature(layer, {
          from_ref: pointProps(start).ref,
          to_ref: ref,
          bundles: defaultBundleFor(true),
          length_override: null,
          length_m: 0,
        });
        group.addLayer(layer);
        attachLayer(layer);
        emitRef.current(true);
        return;
      }

      if (m === "span" && layer instanceof L.Polyline) {
        const lls = flatLatLngs(layer);
        const a = lls.length >= 2 ? nearestNode(lls[0], SNAP_PX) : null;
        const b = lls.length >= 2 ? nearestNode(lls[lls.length - 1], SNAP_PX) : null;
        if (!a || !b) {
          if (map.hasLayer(layer)) map.removeLayer(layer);
          setDrawError("Oba końce przęsła muszą trafiać w narysowane słupy/przyłącza.");
          return;
        }
        // A line drawn THROUGH poles must split on them: every interior VERTEX
        // that hits a pole (≤SNAP_PX, kind=pole) is a split point — otherwise
        // S1→(przez S2)→S3 became one S1–S3 span and S2 stayed unconnected.
        // Passing merely NEAR a pole without a vertex does not split (predictable).
        const chain: L.Marker[] = [a];
        for (let i = 1; i < lls.length - 1; i++) {
          const hit = nearestNode(lls[i], SNAP_PX);
          if (hit && pointProps(hit).kind === "pole" && hit !== chain[chain.length - 1]) {
            chain.push(hit);
          }
        }
        if (b !== chain[chain.length - 1]) chain.push(b);
        if (chain.length < 2) {
          if (map.hasLayer(layer)) map.removeLayer(layer);
          setDrawError("Oba końce przęsła muszą trafiać w narysowane słupy/przyłącza.");
          return;
        }
        for (let i = 1; i < chain.length; i++) {
          if (
            pointProps(chain[i - 1]).kind === "access" &&
            pointProps(chain[i]).kind === "access"
          ) {
            if (map.hasLayer(layer)) map.removeLayer(layer);
            setDrawError("Przęsło łączy słup ze słupem lub słup z przyłączem — nie dwa przyłącza.");
            return;
          }
        }
        // One straight span per consecutive pinned pair; the drawn layer becomes
        // the first segment, the rest are fresh polylines.
        for (let i = 1; i < chain.length; i++) {
          const from = chain[i - 1];
          const to = chain[i];
          const pFrom = pointProps(from);
          const pTo = pointProps(to);
          const isAccess = pFrom.kind === "access" || pTo.kind === "access";
          const props: TechCalcLineProperties = {
            from_ref: pFrom.ref,
            to_ref: pTo.ref,
            bundles: defaultBundleFor(isAccess),
            length_override: null,
            length_m: 0,
          };
          const segment = i === 1 ? layer : L.polyline([], { ...SPAN_STYLE });
          segment.setLatLngs([from.getLatLng(), to.getLatLng()]);
          setFeature(segment, props);
          segment.setStyle(spanStyle(props));
          group.addLayer(segment);
          attachLayer(segment);
        }
        emitRef.current(true);
        return;
      }

      // Stray geometry drawn in a non-drawing mode — drop it.
      if (map.hasLayer(layer)) map.removeLayer(layer);
    });

    return () => {
      map.remove();
      mapRef.current = null;
      setMapInstance(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the map sized to its container (tab layout + docked panel change width).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => mapRef.current?.invalidateSize());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── arm the draw tool for the current mode ─────────────────────────────────
  useEffect(() => {
    modeRef.current = mode;
    const map = mapRef.current;
    if (!map) return;
    map.pm.disableDraw();
    if (mode === "pole") {
      map.pm.enableDraw("Marker", { markerStyle: { icon: nodeIcon("pole", POLE_COLOR) } });
    } else if (mode === "access") {
      // Access line is drawn as a LINE from a pole to the delivery point.
      map.pm.enableDraw("Line", { hintlineStyle: { color: ACCESS_COLOR, dashArray: "4,4" } });
    } else if (mode === "span") {
      map.pm.enableDraw("Line", { hintlineStyle: { color: "#475569", dashArray: "4,4" } });
    } else if (mode === "select") {
      map.pm.enableDraw("Polygon", {
        hintlineStyle: { color: "#f59e0b", dashArray: "4,4" },
        templineStyle: { color: "#f59e0b" },
        pathOptions: { color: "#f59e0b", weight: 2, dashArray: "6 4", fillColor: "#f59e0b", fillOpacity: 0.08 },
        continueDrawing: false,
        // The selection outline must land exactly where aimed — snapping to the
        // drawn features would warp the polygon.
        snappable: false,
      });
    }
    if (mode !== "params") setParamsEditor(null);
    // Leaving "select" drops the selection (spec: wyjście z trybu czyści zaznaczenie).
    if (mode !== "select") {
      selectionHaloRef.current?.clearLayers();
      setSelection([]);
    }
    // Cel Usuń/Edytuj wraca do "Wszystko" poza tymi trybami.
    if (mode !== "edit" && mode !== "remove") setEditTarget("all");
    if (mode !== "copy") setCopySource(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Results tab locks editing.
  useEffect(() => {
    if (showResults) setMode("off");
  }, [showResults]);

  // Blue halo on the copy-source pole.
  useEffect(() => {
    const halo = copyHaloRef.current;
    halo?.clearLayers();
    if (!copySource || mode !== "copy") return;
    const node = findNode(copySource);
    if (node) {
      halo?.addLayer(
        L.circleMarker(node.getLatLng(), {
          radius: 13,
          color: POLE_COLOR,
          weight: 2.5,
          fill: false,
          pmIgnore: true,
        } as L.CircleMarkerOptions),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copySource, mode]);

  // ── per-layer edit / remove / params / copy handlers (no Geoman global modes) ─
  useEffect(() => {
    const group = groupRef.current;
    const map = mapRef.current;
    if (!group || !map) return;
    const layers = group.getLayers();

    const allowNode = editTarget !== "spans";
    const allowSpan = editTarget !== "nodes";

    const removeOnClick = (e: L.LeafletMouseEvent) => {
      const layer = e.target as L.Layer;
      const removeFully = (l: L.Layer) => {
        group.removeLayer(l);
        if (map.hasLayer(l)) map.removeLayer(l);
      };
      if (layer instanceof L.Marker) {
        // Cascade: a node takes its attached spans with it (a dangling span
        // would reference a ref that no longer exists).
        const ref = pointProps(layer).ref;
        for (const l of [...group.getLayers()]) {
          if (!(l instanceof L.Polyline)) continue;
          const p = lineProps(l);
          if (p.from_ref === ref || p.to_ref === ref) removeFully(l);
        }
        removeFully(layer);
      } else if (layer instanceof L.Polyline) {
        // Reverse cascade for access lines: removing the line also removes an
        // A-node left with no remaining span (poles always stay).
        const p = lineProps(layer);
        removeFully(layer);
        for (const ref of [p.from_ref, p.to_ref]) {
          if (!ref.startsWith("A")) continue;
          const stillUsed = group
            .getLayers()
            .some(
              (l) =>
                l instanceof L.Polyline &&
                (lineProps(l).from_ref === ref || lineProps(l).to_ref === ref),
            );
          if (!stillUsed) {
            const node = findNode(ref);
            if (node) removeFully(node);
          }
        }
      } else {
        removeFully(layer);
      }
      emitRef.current(true);
    };

    const paramsOnClick = (e: L.LeafletMouseEvent) => {
      setParamsEditor({ layer: e.target as FeatureLayer });
    };

    const copyPickOnClick = (e: L.LeafletMouseEvent) => {
      const p = pointProps(e.target as L.Layer);
      if (p.kind === "pole") setCopySource(p.ref);
    };

    const pmOf = (layer: L.Layer) =>
      (
        layer as unknown as {
          pm?: {
            enable?: (opts?: Record<string, unknown>) => void;
            disable?: () => void;
            enableLayerDrag?: () => void;
            disableLayerDrag?: () => void;
          };
        }
      ).pm;

    const setLayerPointerEvents = (layer: L.Layer, off: boolean) => {
      const el = (layer as L.Marker | L.Path).getElement?.() as
        | HTMLElement
        | SVGElement
        | undefined;
      if (el) (el as HTMLElement).style.pointerEvents = off ? "none" : "";
    };

    const reset = (layer: L.Layer) => {
      const pm = pmOf(layer);
      try {
        pm?.disable?.();
      } catch {
        /* layer may be detached */
      }
      try {
        pm?.disableLayerDrag?.();
      } catch {
        /* layer may be detached */
      }
      layer.off("click", removeOnClick);
      layer.off("click", paramsOnClick);
      layer.off("click", copyPickOnClick);
      setLayerPointerEvents(layer, false);
    };

    for (const layer of layers) {
      reset(layer);
      const isMarker = layer instanceof L.Marker;
      try {
        const pm = pmOf(layer);
        if (mode === "edit") {
          // Markers: whole-layer drag; spans: vertex edit (whole-line drag would
          // detach both ends at once — endpoints re-snap on pm:edit anyway).
          if (isMarker && allowNode) pm?.enableLayerDrag?.();
          else if (!isMarker && allowSpan)
            pm?.enable?.({ allowSelfIntersection: true, snappable: true, snapDistance: 20 });
        } else if (mode === "remove") {
          if ((isMarker && allowNode) || (!isMarker && allowSpan))
            layer.on("click", removeOnClick);
        } else if (mode === "params") {
          layer.on("click", paramsOnClick);
        } else if (mode === "copy") {
          if (isMarker && pointProps(layer).kind === "pole")
            layer.on("click", copyPickOnClick);
        }
        // Wykluczony cel jest PRZEZROCZYSTY dla zdarzeń — klik przy wspólnym
        // wierzchołku trafia w to, co użytkownik wybrał, a nie w to, co wyżej.
        if (mode === "edit" || mode === "remove") {
          setLayerPointerEvents(layer, (isMarker && !allowNode) || (!isMarker && !allowSpan));
        } else if (mode === "streetview") {
          // Street View may aim at any point, a pole or span included — the click must
          // pass through the drawing to the map (markers don't bubble clicks).
          setLayerPointerEvents(layer, true);
        }
      } catch (err) {
        console.error("Geoman per-layer mode failed", err);
      }
    }

    return () => {
      for (const layer of layers) reset(layer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, editTarget]);

  // ── load the variant's drawing (once per map instance) ─────────────────────
  useEffect(() => {
    const group = groupRef.current;
    if (!group || !initialDrawing || loadedIntoRef.current === group) return;
    loadedIntoRef.current = group;
    for (const f of initialDrawing.features) {
      if (f.geometry.type === "Point") {
        const [lng, lat] = f.geometry.coordinates;
        const props = structuredClone(f.properties) as TechCalcPointProperties;
        const marker = L.marker([lat, lng], { icon: iconForNode(props) });
        (marker as FeatureLayer).feature = { type: "Feature", properties: props };
        group.addLayer(marker);
        attachLayer(marker);
      } else {
        const props = structuredClone(f.properties) as TechCalcLineProperties;
        const line = L.polyline(
          (f.geometry.coordinates as [number, number][]).map(([lng, lat]) => [lat, lng] as [number, number]),
          spanStyle(props),
        );
        (line as FeatureLayer).feature = { type: "Feature", properties: props };
        group.addLayer(line);
        attachLayer(line);
      }
    }
    emit(false);
    if (initialDrawing.features.length) fitToDrawing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDrawing]);

  // ── results / out-of-scope coloring ────────────────────────────────────────
  useEffect(() => {
    restyleNodes();
    // Tooltips carry the "poza opracowaniem" note — keep them in sync too.
    refreshTooltips();
  }, [results, showResults, restyleNodes, refreshTooltips]);

  // Auto-clear the draw error toast.
  useEffect(() => {
    if (!drawError) return;
    const t = setTimeout(() => setDrawError(null), 5000);
    return () => clearTimeout(t);
  }, [drawError]);

  // ── Street View: click a point → click the direction → panorama in a new tab ─
  useStreetViewTool(mapInstance, mode === "streetview", () => setMode("off"));

  // ── GUGiK overlays ─────────────────────────────────────────────────────────
  useGugikToggle(mapRef, gugikParcelsRef, gugik.parcels);
  useGugikToggle(mapRef, gugikInfraRef, gugik.infra);
  useGugikToggle(mapRef, gugikOrthoRef, gugik.ortho);
  useGugikToggle(mapRef, gugikMpzpRef, gugik.mpzp);
  useGugikToggle(mapRef, gugikGdosRef, gugik.gdos);
  useGugikToggle(mapRef, gugikKonserwatorRef, !!gugik.konserwator);
  useGugikToggle(mapRef, gugikKonserwatorEwidencjaRef, !!gugik.konserwatorEwidencja);
  useGugikToggle(mapRef, gugikGddkiaRef, !!gugik.gddkia);
  useGugikToggle(mapRef, gugikClosedAreasRef, !!gugik.closedAreas);
  const gugikLoading = useGugikLoading(
    gugikParcelsRef,
    gugikInfraRef,
    gugikOrthoRef,
    gugik,
    gugikMpzpRef,
    gugikGdosRef,
    gugikKonserwatorRef,
    gugikKonserwatorEwidencjaRef,
    gugikGddkiaRef,
    gugikClosedAreasRef,
  );

  // ── imperative handle (bulk updates from the workspace, e.g. suggestions) ──
  useImperativeHandle(
    handleRef,
    () => ({
      applyPoleParams: (updates: Record<string, Partial<PoleParams>>) => {
        let touched = false;
        for (const node of nodeLayers()) {
          const p = pointProps(node);
          const patch = updates[p.ref];
          if (!patch) continue;
          p.params = { ...p.params, ...patch };
          touched = true;
        }
        if (touched) {
          emit(true);
          restyleNodes();
        }
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // ── docked side panel (params / bulk edit) ─────────────────────────────────
  const editorLayer = paramsEditor?.layer ?? null;
  const editorIsNode = editorLayer instanceof L.Marker;
  const showBulkPanel = mode === "select" && selection.length > 0;
  const sidePanelOpen = showBulkPanel || !!editorLayer;

  const toolBtn = (
    m: EditorMode,
    icon: React.ReactNode,
    label: string,
    title?: string,
  ) => (
    <button
      key={m}
      type="button"
      title={title ?? label}
      onClick={() => setMode(m)}
      className={`flex items-center gap-1 border px-2 py-1 font-mono text-[11px] transition-colors ${
        mode === m
          ? "border-[var(--color-primary)] bg-[var(--color-primary)] text-[var(--color-text-inverse)]"
          : "border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
      }`}
    >
      {icon}
      {label}
    </button>
  );

  const targetBtn = (t: EditTarget, label: string) => (
    <button
      key={t}
      type="button"
      onClick={() => setEditTarget(t)}
      className={`px-1.5 py-1 font-mono text-[10px] transition-colors ${
        editTarget === t
          ? "bg-[var(--color-primary)] text-[var(--color-text-inverse)]"
          : "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className={`flex ${className ?? ""}`}>
      <div className="flex min-w-0 flex-1 flex-col">
        {/* `isolate`: pływające panele mapy (pasek narzędzi, warstwy GUGiK) i same
            warstwy Leafletu siedzą na z-index 1000, więc bez własnego kontekstu
            układania przebijały się NAD modale (z-50) otwierane z tej strony. */}
        <div className="relative isolate min-w-0 flex-1">
          {/* Leaflet permanent-tooltip styles for refs / span lengths. */}
          <style>{`
            .tc-node-label, .tc-line-label {
              background: rgba(255,255,255,0.88); border: 1px solid rgba(0,0,0,0.15);
              border-radius: 2px; padding: 1px 4px; font-family: ui-monospace, monospace;
              font-size: 10px; line-height: 1.25; color: #111; box-shadow: none;
              text-align: center; white-space: nowrap;
            }
            .tc-node-label::before, .tc-line-label::before { display: none; }
            .tc-label-sub { display: block; color: #6b7280; font-size: 9px; }
            .tc-node-icon { background: transparent; border: none; }
          `}</style>

          <div ref={containerRef} className="absolute inset-0 z-0" />

          {/* ── top row: toolbar / results legend + GUGiK panel ─────────────────
              One flex row right of the Leaflet zoom control, so the toolbar wraps before the
              GUGiK panel whatever its width (collapsed, more layers). The row itself lets map
              clicks through — only its children catch them. */}
          <div className="pointer-events-none absolute top-2 left-12 right-2 z-[1000] flex items-start gap-2">
            {/* toolbar (hidden on the results tab) */}
            {!showResults && (
              <div className="pointer-events-auto flex min-w-0 flex-wrap items-center gap-1">
                {toolBtn("pole", <CircleDot size={13} />, "Słup", "Stawiaj słupy (S1, S2…)")}
                {toolBtn(
                  "access",
                  <Home size={13} />,
                  "Przyłącze (linia od słupa)",
                  "Klik 1: słup, klik 2: punkt odbioru — linia kończy się automatycznie",
                )}
                {toolBtn("span", <Spline size={13} />, "Przęsło", "Rysuj przęsło; wierzchołki na słupach dzielą linię na osobne przęsła")}
                {toolBtn(
                  "copy",
                  <Copy size={13} />,
                  "Kopiuj słup",
                  "Klik w słup źródłowy, potem klikaj lokalizacje kopii (parametry bez numerów i adresu)",
                )}
                {toolBtn(
                  "select",
                  <BoxSelect size={13} />,
                  "Zaznacz",
                  "Narysuj poligon wokół słupów — zbiorcza edycja parametrów zaznaczonych",
                )}
                {toolBtn("edit", <Pencil size={13} />, "Edytuj/Przesuń", "Przesuwaj słupy, edytuj wierzchołki przęseł")}
                {toolBtn("remove", <Trash2 size={13} />, "Usuń", "Klik w element usuwa go (słup zabiera swoje przęsła)")}
                {(mode === "edit" || mode === "remove") && (
                  <span
                    className="inline-flex items-center border border-[var(--color-border)] bg-[var(--color-bg-surface)]"
                    title="Słupy i przęsła mają wspólne wierzchołki — wybierz, w co celuje klik"
                  >
                    <span className="px-1.5 font-mono text-[10px] text-[var(--color-text-tertiary)]">
                      Cel:
                    </span>
                    {targetBtn("nodes", "Słupy")}
                    {targetBtn("spans", "Przęsła")}
                    {targetBtn("all", "Wszystko")}
                  </span>
                )}
                {toolBtn("params", <SlidersHorizontal size={13} />, "Parametry", "Klik w element otwiera panel parametrów obok mapy")}
                {toolBtn("streetview", <PersonStanding size={13} />, "Street View", "Street View — kliknij punkt, potem kierunek")}
                {toolBtn("off", <MousePointer2 size={13} />, "Wskaźnik", "Tylko podgląd / nawigacja mapą")}
                <button
                  type="button"
                  title="Dopasuj widok do rysunku"
                  onClick={fitToDrawing}
                  className="border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-1 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
                >
                  <Maximize2 size={13} />
                </button>
              </div>
            )}
            {showResults && (
              <div className="pointer-events-auto flex min-w-0 flex-wrap items-center gap-2 border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2 py-1 font-mono text-[10px] text-[var(--color-text-secondary)]">
                <span className="inline-flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-full" style={{ background: OK_COLOR }} /> warunek spełniony</span>
                <span className="inline-flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-full" style={{ background: FAIL_COLOR }} /> warunek niespełniony</span>
                <span className="inline-flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-full" style={{ background: NA_COLOR }} /> błąd / nieliczony</span>
                <span className="inline-flex items-center gap-1">
                  <i className="h-2.5 w-2.5 rounded-full border-2 bg-white" style={{ borderColor: NA_COLOR }} /> poza opracowaniem
                </span>
                <button
                  type="button"
                  title="Street View — kliknij punkt, potem kierunek"
                  onClick={() => setMode((m) => (m === "streetview" ? "off" : "streetview"))}
                  className={
                    mode === "streetview"
                      ? "text-[var(--color-primary)]"
                      : "text-[var(--color-text-secondary)] hover:text-[var(--color-primary)]"
                  }
                >
                  <PersonStanding size={13} />
                </button>
                <button
                  type="button"
                  title="Dopasuj widok do rysunku"
                  onClick={fitToDrawing}
                  className="text-[var(--color-text-secondary)] hover:text-[var(--color-primary)]"
                >
                  <Maximize2 size={13} />
                </button>
              </div>
            )}

            <GugikLayerPanel
              gugik={gugik}
              onChange={setGugik}
              zoom={zoom}
              loading={gugikLoading}
              className="pointer-events-auto ml-auto shrink-0"
            />
          </div>

          {drawError && (
            <div className="absolute bottom-3 left-1/2 z-[1100] -translate-x-1/2 border border-[var(--color-primary)] bg-[var(--color-bg-surface)] px-3 py-1.5 font-mono text-[11px] text-[var(--color-primary)] shadow-lg">
              {drawError}
            </div>
          )}

          {/* ── mode hints ─────────────────────────────────────────────────────── */}
          {mode === "select" && selection.length === 0 && !drawError && (
            <div className="absolute bottom-3 left-1/2 z-[1000] -translate-x-1/2 border border-amber-600 bg-[var(--color-bg-surface)] px-3 py-1.5 font-mono text-[11px] text-amber-600 shadow-lg">
              Narysuj poligon wokół słupów do zbiorczej edycji.
            </div>
          )}
          {mode === "streetview" && !drawError && (
            <div className="absolute bottom-3 left-1/2 z-[1000] -translate-x-1/2 border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-1.5 font-mono text-[11px] text-[var(--color-text-secondary)] shadow-lg">
              Street View — kliknij punkt, potem kierunek
            </div>
          )}
          {mode === "copy" && !drawError && (
            <div className="absolute bottom-3 left-1/2 z-[1000] -translate-x-1/2 border border-blue-600 bg-[var(--color-bg-surface)] px-3 py-1.5 font-mono text-[11px] text-blue-600 shadow-lg">
              {copySource
                ? `Źródło: ${copySource} — klikaj lokalizacje kopii (klik w inny słup zmienia źródło).`
                : "Kliknij słup źródłowy, potem klikaj lokalizacje kopii."}
            </div>
          )}
        </div>

        {/* ── QGIS-like status bar: cursor coords / scale / display EPSG ───────── */}
        <MapStatusBar map={mapInstance} storageKey="techCalc.statusBar.crs" />
      </div>

      {/* ── docked side panel: the map narrows instead of being covered ──────── */}
      {sidePanelOpen && (
        <div className="w-80 shrink-0 overflow-y-auto border-l border-[var(--color-border)] bg-[var(--color-bg-surface)]">
          {showBulkPanel && (
            <BulkParamsPanel
              key={selection.join(",")}
              count={selection.length}
              dictionaries={dictionaries}
              onApply={(patch) => {
                const selected = new Set(selection);
                for (const node of nodeLayers()) {
                  const p = pointProps(node);
                  if (selected.has(p.ref)) p.params = { ...p.params, ...patch };
                }
                emit(true);
                restyleNodes();
                selectionHaloRef.current?.clearLayers();
                setSelection([]);
              }}
              onClose={() => {
                selectionHaloRef.current?.clearLayers();
                setSelection([]);
              }}
            />
          )}
          {!showBulkPanel && editorLayer && editorIsNode && (
            <PoleParamsPanel
              key={pointProps(editorLayer).ref}
              poleRef={pointProps(editorLayer).ref}
              kind={pointProps(editorLayer).kind}
              initial={pointProps(editorLayer).params}
              address={pointProps(editorLayer).address}
              dictionaries={dictionaries}
              onCommit={(params) => {
                pointProps(editorLayer).params = params;
                emit(true);
                restyleNodes();
              }}
              onClose={() => setParamsEditor(null)}
            />
          )}
          {!showBulkPanel && editorLayer && !editorIsNode && (
            <SpanParamsPanel
              key={`${lineProps(editorLayer).from_ref}-${lineProps(editorLayer).to_ref}`}
              fromRef={lineProps(editorLayer).from_ref}
              toRef={lineProps(editorLayer).to_ref}
              lengthM={lineProps(editorLayer).length_m}
              initialBundles={lineProps(editorLayer).bundles}
              initialOverride={lineProps(editorLayer).length_override}
              dictionaries={dictionaries}
              onCommit={(patch) => {
                Object.assign(lineProps(editorLayer), patch);
                emit(true);
              }}
              onClose={() => setParamsEditor(null)}
            />
          )}
        </div>
      )}
    </div>
  );
});
